import type { PopulatedOrgMember, TaskEvent, TaskEventChange, User } from "@api/types";
import { useAuthStore } from "@lib/auth";
import { formatDateRelative } from "@lib/utils";
import { History } from "lucide-react";
import { useMemo } from "react";
import { useTaskEvents } from "@/api/task";
import { TableLoading } from "@/ui/elements";
import { getUserName, TASK_STATUS_LABELS, TaskUserAvatar } from "./shared";

function findChange(changes: TaskEventChange[] | undefined, key: string) {
  return changes?.find((change) => change.key === key);
}

function describeEvent(event: TaskEvent, userById: Map<string, User>): string {
  const actor = event.user ? getUserName(userById.get(event.user)) : "System";
  switch (event.type) {
    case "created":
      return `${actor} created this task`;
    case "commented":
      return `${actor} commented`;
    case "assigned": {
      const change = findChange(event.changes, "user");
      const assignee = change?.newValue ? userById.get(change.newValue) : null;
      return assignee
        ? `${actor} assigned ${getUserName(assignee)}`
        : `${actor} unassigned this task`;
    }
    case "status_changed": {
      const change = findChange(event.changes, "status");
      const from = TASK_STATUS_LABELS[change?.oldValue as keyof typeof TASK_STATUS_LABELS];
      const to = TASK_STATUS_LABELS[change?.newValue as keyof typeof TASK_STATUS_LABELS];
      if (from && to) {
        return `${actor} changed status from ${from} to ${to}`;
      }
      return `${actor} changed the status`;
    }
    case "updated": {
      const keys = (event.changes ?? []).map((change) =>
        change.key === "dueDate"
          ? "due date"
          : change.key === "snoozedUntil"
            ? "snooze date"
            : change.key === "customer"
              ? "contact"
              : change.key === "thread"
                ? "conversation"
                : change.key,
      );
      return keys.length ? `${actor} updated ${keys.join(", ")}` : `${actor} updated this task`;
    }
    default:
      return `${actor} updated this task`;
  }
}

export function TaskHistory({
  taskId,
  members,
}: {
  taskId?: string;
  members?: PopulatedOrgMember[];
}) {
  const { data: events, isLoading } = useTaskEvents(taskId);
  const authUser = useAuthStore((state) => state.user);

  // Include the signed-in user so their own actions resolve even when they
  // aren't in the gym's member list (e.g. org-level admins).
  const userById = useMemo(() => {
    const map = new Map((members ?? []).map((member) => [member.user.id, member.user]));
    if (authUser && !map.has(authUser.id)) map.set(authUser.id, authUser);
    return map;
  }, [members, authUser]);

  if (isLoading && !events) {
    return <TableLoading className="border-0" />;
  }

  if (!events?.length) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <History className="size-6 text-muted-foreground/70" />
        <p className="text-sm text-muted-foreground">No history yet.</p>
      </div>
    );
  }

  return (
    <ol className="flex flex-col gap-4">
      {events.map((event) => (
        <li key={event.id} className="flex items-start gap-2.5">
          <TaskUserAvatar user={event.user ? userById.get(event.user) : null} className="mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm text-foreground">{describeEvent(event, userById)}</p>
            <p className="text-xs text-muted-foreground">{formatDateRelative(event.createdAt)}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}
